/**
 * Layout shell: "app" = colonne téléphone (app-width, défaut) ;
 * "wide" = pleine largeur viewport (opt-in desktop / preview).
 * Contrôle et mode wide réservés au dev local.
 */

import environment from 'config/environment'

export const LAYOUT_MODE_KEY = 'debug:layoutMode'
export type LayoutMode = 'app' | 'wide'

export const LAYOUT_MODE_EVENT = 'dg-layout-mode-change'

/** Toggle menu + mode wide : uniquement en dev local (Vite DEV ou BUILD_ENV=local). */
export function isLayoutModeControlEnabled(): boolean {
  return import.meta.env.DEV === true || environment.isLocal
}

export function getLayoutMode(): LayoutMode {
  if (!isLayoutModeControlEnabled()) {
    return 'app'
  }
  try {
    return localStorage.getItem(LAYOUT_MODE_KEY) === 'wide' ? 'wide' : 'app'
  } catch {
    return 'app'
  }
}

export function isLayoutWide(): boolean {
  return getLayoutMode() === 'wide'
}

export function setLayoutMode(mode: LayoutMode): void {
  const effective: LayoutMode = isLayoutModeControlEnabled() ? mode : 'app'
  try {
    if (effective === 'wide') {
      localStorage.setItem(LAYOUT_MODE_KEY, 'wide')
    } else {
      localStorage.removeItem(LAYOUT_MODE_KEY)
    }
  } catch {
    // ignore
  }
  applyLayoutModeToDocument(effective)
  try {
    window.dispatchEvent(new CustomEvent(LAYOUT_MODE_EVENT, { detail: { mode: effective } }))
  } catch {
    // ignore
  }
}

export function toggleLayoutMode(): LayoutMode {
  if (!isLayoutModeControlEnabled()) {
    setLayoutMode('app')
    return 'app'
  }
  const next: LayoutMode = isLayoutWide() ? 'app' : 'wide'
  setLayoutMode(next)
  return next
}

export function applyLayoutModeToDocument(mode: LayoutMode = getLayoutMode()): void {
  if (typeof document === 'undefined') return
  const effective: LayoutMode = isLayoutModeControlEnabled() ? mode : 'app'
  document.documentElement.classList.toggle('layout-wide', effective === 'wide')
  document.documentElement.classList.toggle('layout-app', effective !== 'wide')
}

/** Apply ?layout=wide|app from URL (before first paint when possible). */
export function applyLayoutModeFromLocation(): void {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)

  if (!isLayoutModeControlEnabled()) {
    if (params.has('layout')) {
      params.delete('layout')
      const qs = params.toString()
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
      window.history.replaceState(null, '', next)
    }
    applyLayoutModeToDocument('app')
    return
  }

  const layout = params.get('layout')?.trim().toLowerCase()
  if (layout === 'wide' || layout === 'app') {
    setLayoutMode(layout)
    params.delete('layout')
    const qs = params.toString()
    const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', next)
    return
  }
  applyLayoutModeToDocument()
}
